// src/lib/resa-invoice-month.js — Plan B (ADR-182)
//
// ⚠️ DUPLICATION CONTRÔLÉE — Pattern ADR-021
//
// Ce fichier est la version ESM de lib/resa-invoice-month.js (CJS).
// Toute modification DOIT être appliquée AUX DEUX fichiers (ESM + CJS).
// Cf. ADR-021 dans docs/handoff/architecture/decisions-log.md
// Un test de parité (resa-invoice-month.test.js) exécute les mêmes vecteurs
// sur les deux copies et exige une égalité stricte.
//
// Règle-mois « majorité de nuits » + override ◀▶ par relocation physique
// (déplacement de l'objet résa entre buckets data['YYYY-MM'][lid] + pose
// month_override). UTC PUR : jamais setDate/getDate locaux (corrige le bug
// DST silencieux de l'ancien getResaInvoiceMonth TZ-local).

// Plancher dur du mois de facturation. Pas de fenêtre glissante.
export const INVOICE_MONTH_FLOOR = '2026-04'

// ── addDaysISO(iso, n) : 'YYYY-MM-DD' + n jours, UTC pur ──
// Date.UTC(y, m-1, d) + n*86400000, reformat via getUTC*. JAMAIS setDate local.
// iso invalide → null.
export function addDaysISO(iso, n) {
  if (!iso || typeof iso !== 'string') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const base = Date.UTC(y, mo - 1, d)
  if (isNaN(base)) return null
  const dt = new Date(base + (n || 0) * 86400000)
  if (isNaN(dt.getTime())) return null
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

// ── addMonthsMk(mk, dir) : 'YYYY-MM' ± N mois, UTC ──
// Gère déc→jan et jan→déc. mk invalide → null.
export function addMonthsMk(mk, dir) {
  if (!mk || typeof mk !== 'string') return null
  const m = /^(\d{4})-(\d{2})$/.exec(mk)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  // Index mois 0-based, ajout dir, normalisation via Date.UTC.
  const dt = new Date(Date.UTC(y, mo - 1 + (dir || 0), 1))
  if (isNaN(dt.getTime())) return null
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  return `${yy}-${mm}`
}

// ── nightsBetween(checkIn, checkOut) : nombre de nuits (entier) ──
// start inclus, end exclu. UTC pur (jamais setDate/getDate locaux). Dates
// absentes / end ≤ start / invalides → fallback 1 (jamais 0 nuit facturée).
// Helper mutualisé (F5 Lot M3.0) — source unique pour le calcul de nuits ;
// resas-v2-source.js le réutilise au lieu de redupliquer le calcul.
export function nightsBetween(checkIn, checkOut) {
  const start = addDaysISO(checkIn, 0)
  const end = addDaysISO(checkOut, 0)
  if (!start || !end) return 1
  const d = Math.round(
    (Date.parse(end + 'T00:00:00Z') - Date.parse(start + 'T00:00:00Z')) /
      86400000
  )
  return d > 0 ? d : 1
}

// ── computeInvoiceMonthByNights(startISO, endISO) : 'YYYY-MM' | null ──
// Majorité de nuits (start inclus, end exclu). Tri nb desc puis mk asc
// (égalité → mois de check-in). end absent/≤start/invalide → mois de start.
// !start → null.
export function computeInvoiceMonthByNights(startISO, endISO) {
  if (!startISO) return null
  const start = addDaysISO(startISO, 0)
  if (!start) return null
  const startMk = start.slice(0, 7)
  const end = endISO ? addDaysISO(endISO, 0) : null
  // Cas dégénéré (end absent, invalide ou ≤ start) → mois du check-in.
  if (!end || end <= start) return startMk
  const nuitsParMois = {}
  let cur = start
  // Itère jour par jour (start inclus, end exclu).
  while (cur && cur < end) {
    const mk = cur.slice(0, 7)
    nuitsParMois[mk] = (nuitsParMois[mk] || 0) + 1
    cur = addDaysISO(cur, 1)
  }
  const sorted = Object.entries(nuitsParMois)
    .sort(([mkA, nA], [mkB, nB]) => (nB - nA) || mkA.localeCompare(mkB))
  return sorted[0]?.[0] ?? null
}

// ── daysInMonthMk(mk) : nombre de jours du mois "YYYY-MM" (UTC pur) ──
// 0 si mk invalide. Le jour 0 du mois+1 = dernier jour du mois (UTC).
export function daysInMonthMk(mk) {
  if (!mk || typeof mk !== 'string') return 0
  const m = /^(\d{4})-(\d{2})$/.exec(mk)
  if (!m) return 0
  const y = Number(m[1])
  const mo = Number(m[2])
  const dt = new Date(Date.UTC(y, mo, 0))
  if (isNaN(dt.getTime())) return 0
  return dt.getUTCDate()
}

// ── nightsInMonth(resa, mk) : nuits du séjour tombant dans le mois `mk` ──
// Option B (occupation/nuits-louées en « nuits réelles par mois »). UTC PUR.
// start inclus / end exclu. Plafonné à daysInMonthMk(mk) (jamais > 100 %).
//
// Fin du séjour DÉRIVÉE (256 résas KV ont checkOut=null) :
//   end = checkOut valide ? checkOut : addDaysISO(checkIn, r.nuits)
// (sinon le séjour serait borné à 1 nuit → sous-comptage massif).
//
// Signature : OBJET résa entier (PAS (checkIn, checkOut, mk)) — décision
// pessimist G1. Réutilise addDaysISO du fichier. mk ou checkIn invalide → 0.
export function nightsInMonth(resa, mk) {
  if (!resa) return 0
  if (!mk || typeof mk !== 'string' || !/^\d{4}-\d{2}$/.test(mk)) return 0
  const checkIn = resa.checkIn || resa.date
  const start = addDaysISO(checkIn, 0)
  if (!start) return 0
  // Fin dérivée : checkOut valide (et > start) sinon checkIn + r.nuits.
  let end = resa.checkOut ? addDaysISO(resa.checkOut, 0) : null
  if (!end || end <= start) {
    const n = Number(resa.nuits)
    end = addDaysISO(start, Number.isFinite(n) && n > 0 ? n : 1)
  }
  if (!end) return 0
  // Bornes UTC du mois mk : [monthStart, monthEnd[ (end exclu).
  const monthStart = mk + '-01'
  const monthEnd = addMonthsMk(mk, 1) + '-01'
  // Intersection [start, end[ ∩ [monthStart, monthEnd[, en nuits.
  const from = start > monthStart ? start : monthStart
  const to = end < monthEnd ? end : monthEnd
  if (to <= from) return 0
  const nights = Math.round(
    (Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / 86400000
  )
  const cap = daysInMonthMk(mk)
  return Math.min(Math.max(0, nights), cap)
}

// ── resolveInvoiceMonth(resa) : 'YYYY-MM' | null ──
// month_override (si 'YYYY-MM' valide) > computeInvoiceMonthByNights >
// slice(0,7) du check-in. !start → null.
export function resolveInvoiceMonth(resa) {
  if (!resa) return null
  const ov = resa.month_override
  if (typeof ov === 'string' && /^\d{4}-\d{2}$/.test(ov)) return ov
  const start = resa.checkIn || resa.date
  const byNights = computeInvoiceMonthByNights(start, resa.checkOut)
  if (byNights) return byNights
  return (typeof start === 'string' ? start.slice(0, 7) : null) || null
}

// ── Gardes plancher (comparaison lexicographique 'YYYY-MM') ──
export function canEditMonth(mk) {
  return typeof mk === 'string' && mk >= INVOICE_MONTH_FLOOR
}
export function canCreateMonth(mk) {
  return typeof mk === 'string' && mk >= '2026-05'
}
export function canMoveTarget(mk) {
  return typeof mk === 'string' && mk >= '2026-05'
}

// ── resaIdentityKey(resa) : clé identité stable (survie réimport) ──
// 'sh:'+superhote_id > 'ab:'+airbnbCode > 'bk:'+bookingRef > null.
// external_id EXCLU (0 occurrence KV, réservé V2).
export function resaIdentityKey(resa) {
  if (!resa) return null
  if (resa.superhote_id) return 'sh:' + resa.superhote_id
  if (resa.airbnbCode) return 'ab:' + resa.airbnbCode
  if (resa.bookingRef) return 'bk:' + resa.bookingRef
  return null
}

// ── isRelocatable(resa) : ré-ancrable (éligibilité ◀▶) ──
// true ssi ical_uid présent OU resaIdentityKey non null.
export function isRelocatable(resa) {
  if (!resa) return false
  if (resa.ical_uid) return true
  return resaIdentityKey(resa) !== null
}

// ── findResaBucket(data, lid, id) : { mk, idx } | null ──
// Parcourt les clés 'YYYY-MM' uniquement (skip clés non-mois). Premier hit.
export function findResaBucket(data, lid, id) {
  if (!data || !lid || id == null) return null
  for (const mk of Object.keys(data)) {
    if (!/^\d{4}-\d{2}$/.test(mk)) continue
    const resas = data[mk]?.[lid]?.reservations
    if (!Array.isArray(resas)) continue
    const idx = resas.findIndex(r => r.id === id)
    if (idx >= 0) return { mk, idx }
  }
  return null
}

// ── moveResaMonth(data, lid, resaId, dir) : { data, ok, reason } ──
// Relocation physique ±1 mois adjacent au mois NATUREL. 100% synchrone,
// immutable (deep clone, ne mute pas data en entrée).
// reasons : 'not_found' | 'amplitude' | 'floor' | 'collision'.
export function moveResaMonth(data, lid, resaId, dir) {
  const nd = JSON.parse(JSON.stringify(data || {}))
  const loc = findResaBucket(nd, lid, resaId)
  if (!loc) return { data: nd, ok: false, reason: 'not_found' }
  const resa = nd[loc.mk][lid].reservations[loc.idx]
  const targetMk = addMonthsMk(loc.mk, dir)
  if (!targetMk) return { data: nd, ok: false, reason: 'not_found' }
  // Mois naturel (majorité de nuits) — base de l'amplitude ±1.
  const naturalMk = computeInvoiceMonthByNights(resa.checkIn || resa.date, resa.checkOut)
  const allowed = new Set([
    addMonthsMk(naturalMk, -1),
    naturalMk,
    addMonthsMk(naturalMk, 1),
  ])
  if (!allowed.has(targetMk)) return { data: nd, ok: false, reason: 'amplitude' }
  if (!canMoveTarget(targetMk)) return { data: nd, ok: false, reason: 'floor' }
  // Collision : une résa de même clé identité (non null) existe déjà au mois cible.
  const key = resaIdentityKey(resa)
  if (key) {
    const targetResas = nd[targetMk]?.[lid]?.reservations || []
    if (targetResas.some(r => resaIdentityKey(r) === key)) {
      return { data: nd, ok: false, reason: 'collision' }
    }
  }
  // Splice de l'ancien bucket.
  nd[loc.mk][lid].reservations.splice(loc.idx, 1)
  // Pose/supprime month_override selon retour ou non au naturel.
  if (targetMk === naturalMk) {
    delete resa.month_override
  } else {
    resa.month_override = targetMk
  }
  // Ensure + push dans le bucket cible.
  if (!nd[targetMk]) nd[targetMk] = {}
  if (!nd[targetMk][lid]) nd[targetMk][lid] = { reservations: [], extras: [] }
  if (!Array.isArray(nd[targetMk][lid].reservations)) nd[targetMk][lid].reservations = []
  nd[targetMk][lid].reservations.push(resa)
  return { data: nd, ok: true, reason: null }
}
